import {eq} from '@shared/helpers/lang'
import {createStateMaker} from '@shared/helpers/object'

import {GeneralSkip, PayMethod, PayMethodType, Service} from '@web/core/constants'

import {PaymentMethodState} from '../interfaces'

export const getInitialState = (): PaymentMethodState => ({
    availableTypes: [],
    defaultPayMethod: PayMethod.NONE,
    defaultNormalPayMethod: null,
    selected: {
        payMethods: [],
        prevPayMethod: PayMethod.NONE,
        payNextMethod: GeneralSkip.VIRTUAL_ACCOUNT,
    },
    complex: {
        selectionType: 'CONCURRENT',
        availableService: false,
    },
    statements: {},
    errorExposureArea: [],
    shopBill: {
        resultType: null,
        authToken: '',
        svcType: Service.C1,
        cpId: '',
        itemId: '',
        sellerId: '',
        title: '',
    },
    verifyServerClientData: '',
    scene: 'IDLE',
})

export const getAvailablePayMethodTypes = ({
    availablePayMethodTypes,
    pointRestrictPolicy = 'DEFAULT',
}: {
    availablePayMethodTypes: PayMethodType[]
    pointRestrictPolicy?: 'DEFAULT' | 'EACH'
}) => {
    const isEachPointRestrictable = eq(pointRestrictPolicy, 'EACH')
    const pointMethods: PayMethodType[] = [PayMethod.REWARD_POINT, PayMethod.CHARGE_POINT]
    const payPointMethods: PayMethodType[] = [
        PayMethod.REWARD_POINT,
        PayMethod.CHARGE_POINT,
        PayMethod.PAY_LATER,
        PayMethod.RECHARGE_POINT,
    ]
    const canBePayPointMethod = pointMethods.every((payMethod) => availablePayMethodTypes.includes(payMethod))

    return availablePayMethodTypes.filter((availablePayMethodType) => {
        /**
         * 1. Policy 가 DEFAULT 인 경우
         * REWARD_POINT(적립포인트), CHARGE_POINT(충전포인트) 중 하나라도
         * 사용 가능 결제수단에서 제외되는 경우
         * 포인트 영역 미노출 & 결제수단 충전결제 영역 미노출 처리
         * @see https://oss.fin.navercorp.com/payOrder/order-qa/issues/4589#issuecomment-758748
         *
         * 2. Policy 가 EACH 인 경우
         * @see https://wiki.fin.navercorp.com/pages/viewpage.action?pageId=272761335&spaceKey=FIN&title=250709%2BC2%2B%EC%A3%BC%EB%AC%B8%EC%84%9C%2B%EA%B2%B0%EC%A0%9C%EC%88%98%EB%8B%A8%2B%EC%A0%9C%ED%95%9C%2B%EC%B9%B4%ED%85%8C%EA%B3%A0%EB%A6%AC%2B%ED%8F%AC%EC%9D%B8%ED%8A%B8%EC%99%80%2B%EB%A8%B8%EB%8B%88%2B%EC%B6%A9%EC%A0%84%EA%B2%B0%EC%A0%9C%2B%EB%B6%84%EB%A6%AC
         */
        if (payPointMethods.includes(availablePayMethodType) && !isEachPointRestrictable) {
            return canBePayPointMethod
        }
        if (availablePayMethodType === PayMethod.SKIP) {
            // 나중에결제 사용 불가
            return false
        }
        return true
    })
}

const NormalPayMethod: PayMethodType[] = [
    PayMethod.CARD,
    PayMethod.MOBILE,
    PayMethod.MOBILE_EASY_PAY,
    PayMethod.VIRTUAL_ACCOUNT,
]

export function getDefaultNormalPayMethod({
    recentPayMethodType,
    availablePayMethodTypes,
}: {
    recentPayMethodType: Maybe<PayMethodType>
    availablePayMethodTypes: PayMethodType[]
}): Maybe<PayMethodType> {
    const defaultNormalPayMethod = NormalPayMethod.find((payMethod) => availablePayMethodTypes.includes(payMethod))

    if (defaultNormalPayMethod && recentPayMethodType && NormalPayMethod.includes(recentPayMethodType)) {
        return defaultNormalPayMethod
    }
    // 최근 결제수단이 일반 결제수단이 아니라면, 가장 첫번째 결제수단인 삼성페이 노출
    return null
}

const DefaultPayMethods: PayMethodType[] = [
    PayMethod.CARD_EASY_PAY,
    PayMethod.BANK_EASY_PAY,
    PayMethod.CARD,
    PayMethod.MOBILE,
    PayMethod.MOBILE_EASY_PAY,
    PayMethod.VIRTUAL_ACCOUNT,
    PayMethod.SAMSUNG_PAY,
    PayMethod.FAMILY_PAY,
]

export function getDefaultPaymentMethod({
    payMethodType,
    nextPayMethod,
    availablePayMethodTypes,
}: {
    payMethodType?: PayMethodType
    nextPayMethod: PayMethodType
    availablePayMethodTypes: PayMethodType[]
}) {
    if (payMethodType && availablePayMethodTypes.includes(payMethodType) && DefaultPayMethods.includes(payMethodType)) {
        return payMethodType
    }
    if (availablePayMethodTypes.includes(nextPayMethod) && DefaultPayMethods.includes(nextPayMethod)) {
        return nextPayMethod
    }
    return (
        DefaultPayMethods.find((defaultPayMethod) => {
            return availablePayMethodTypes.some((availablePayMethod) => defaultPayMethod === availablePayMethod)
        }) ?? PayMethod.NONE
    )
}

export const makeMock = createStateMaker<PaymentMethodState>(getInitialState)
